import React, { useState } from 'react';
import { createApi } from 'unsplash-js';
import { Search, Download, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';

const unsplash = createApi({
  accessKey: '79q_Tj5GsX6PnSSSiPT-wuMBouw1yNZP7A8miSgcJK4'
});

interface ImageSearchProps {
  onError?: (error: string) => void;
}

const ImageSearch: React.FC<ImageSearchProps> = ({ onError }) => {
  const [query, setQuery] = useState('');
  const [translatedQuery, setTranslatedQuery] = useState('');
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchMode, setSearchMode] = useState<'unsplash' | 'pixabay' | 'ai' | 'huggingface'>('pixabay');
  const [hfModel, setHfModel] = useState('black-forest-labs/FLUX.1-dev');
  const { subscription } = useAuthStore();

  const isSubscriptionLimited = subscription?.plan === 'essential' || subscription?.plan === 'initial';
  const hasRequestsAvailable = subscription?.request_count && subscription.request_count > 0;

  const translations: Record<string, string> = {
    'leão': 'lion',
    'um leão': 'a lion',
    'academia': 'gym',
    'uma academia': 'a gym',
    'praia': 'beach',
    'montanha': 'mountain',
    'cidade': 'city',
    'floresta': 'forest',
    'céu': 'sky',
    'mar': 'sea',
    'rio': 'river',
    'lago': 'lake',
    'parque': 'park',
    'jardim': 'garden',
    'neve': 'snow',
    'chuva': 'rain',
    'sol': 'sun',
    'noite': 'night',
    'dia': 'day',
    'estrada': 'road',
    'rua': 'street',
    'cachorro': 'dog',
    'gato': 'cat',
    'pássaro': 'bird',
    'carro': 'car',
    'bicicleta': 'bicycle',
    'escola': 'school',
    'casa': 'house',
    'comida': 'food',
    'restaurante': 'restaurant',
    'café': 'coffee',
    'água': 'water',
    'pessoas': 'people',
    'família': 'family',
    'amigos': 'friends',
    'trabalho': 'work',
    'computador': 'computer',
    'telefone': 'phone',
    'dinheiro': 'money',
    'livro': 'book',
    'filme': 'movie',
    'música': 'music',
    'arte': 'art',
    'esporte': 'sport',
    'futebol': 'soccer',
    'basquete': 'basketball',
    'tênis': 'tennis',
    'prédio': 'building',
    'escritório': 'office',
    'médico': 'doctor',
    'hospital': 'hospital',
    'universidade': 'university',
    'viagem': 'travel',
    'férias': 'vacation',
    'avião': 'airplane',
    'trem': 'train',
    'ônibus': 'bus',
    'barco': 'boat',
    'natureza': 'nature',
    'animal': 'animal',
    'planta': 'plant',
    'flor': 'flower',
    'árvore': 'tree',
    'bolsas de luxo': 'luxury bag',
    'luxo': 'luxury',
    'vermelho': 'red',
    'azul': 'blue',
    'verde': 'green',
    'amarelo': 'yellow',
    'preto': 'black',
    'branco': 'white',
    'roxo': 'purple',
    'rosa': 'pink',
    'laranja': 'orange',
    'marrom': 'brown',
    'cinza': 'gray',
    'brasil': 'brazil',
    'estados unidos': 'united states',
    'frança': 'france',
    'itália': 'italy',
    'japão': 'japan',
    'china': 'china',
    'espanha': 'spain',
    'alemanha': 'germany',
    'reino unido': 'united kingdom',
    'canadá': 'canada',
    'méxico': 'mexico',
    'austrália': 'australia',
    'rússia': 'russia',
    'índia': 'india',
    'tecnologia': 'technology',
    'negócios': 'business',
    'saúde': 'health',
    'educação': 'education',
    'ciência': 'science',
    'religião': 'religion',
    'política': 'politics',
    'economia': 'economy',
    'história': 'history',
    'geografia': 'geography',
    'matemática': 'mathematics',
    'física': 'physics',
    'química': 'chemistry',
    'biologia': 'biology',
    'literatura': 'literature',
    'arquitetura': 'architecture',
    'design': 'design',
    'moda': 'fashion',
    'alimento': 'food',
    'bebida': 'drink',
    'cozinha': 'kitchen',
    'banheiro': 'bathroom',
    'quarto': 'bedroom',
    'sala': 'living room',
    'janela': 'window',
    'porta': 'door',
    'parede': 'wall',
    'teto': 'ceiling',
    'piso': 'floor'
  };

  const translateQuery = (text: string): string => {
    const lowerText = text.toLowerCase().trim();
    
    if (translations[lowerText]) {
      return translations[lowerText];
    }
    
    const words = lowerText.split(/\s+/);
    const translatedWords = words.map(word => {
      return translations[word] || word;
    });
    
    return translatedWords.join(' ');
  };

  const searchPixabayImages = async (query: string) => {
    try {
      const pixabayApiKey = '50187623-70eb9b7930604fb676b1ab4a6';
      const encodedQuery = encodeURIComponent(query);
      const apiUrl = `https://pixabay.com/api/?key=${pixabayApiKey}&q=${encodedQuery}&image_type=photo&orientation=horizontal&per_page=20&safesearch=true`;
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`Erro na API Pixabay: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.hits || data.hits.length === 0) {
        return [];
      }
      
      return data.hits.map((hit: any) => ({
        id: hit.id,
        urls: {
          regular: hit.webformatURL,
          full: hit.largeImageURL,
          raw: hit.imageURL || hit.largeImageURL,
        },
        alt_description: `Imagem de ${query}`,
      }));
    } catch (error) {
      console.error('Erro ao buscar imagens do Pixabay:', error);
      throw new Error(
        error instanceof Error ? 
        error.message : 
        'O serviço do Pixabay está indisponível no momento. Tente novamente mais tarde.'
      );
    }
  };

  const generateImageWithAI = async (prompt: string) => {
    try {
      const enhancedPrompt = `${prompt} com qualidade perfeita, alta resolução`;

      const formData = new FormData();
      formData.append('text', enhancedPrompt);

      const response = await fetch("https://api.deepai.org/api/text2img", {
        method: "POST",
        headers: {
          "api-key": "b32a4f98-039e-4a03-b624-6db02566f19e"
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.output || 
          errorData.err || 
          `Erro na geração de imagens: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!data.output_url) {
        throw new Error('Nenhuma imagem gerada para este prompt');
      }

      const imageResponse = await fetch(data.output_url);
      const blob = await imageResponse.blob();
      const base64Image = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });

      return [{
        urls: {
          regular: base64Image,
          full: base64Image
        },
        alt_description: `Imagem gerada por IA para: ${prompt}`
      }];
    } catch (error) {
      console.error('Erro ao gerar imagens com IA:', error);
      throw new Error(
        error instanceof Error ? 
        error.message : 
        'O serviço de geração de imagens está indisponível no momento. Tente novamente mais tarde.'
      );
    }
  };

  const generateImageWithHuggingFace = async (prompt: string, model: string) => {
    try {
      if (isSubscriptionLimited && !hasRequestsAvailable) {
        throw new Error('Você atingiu o limite de requisições do seu plano');
      }

      const tokens = ["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", "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"];
      const currentTokenIndex = Math.floor(Math.random() * tokens.length);
      const API_TOKEN = tokens[currentTokenIndex];
      
      const enhancedPrompt = translateQuery(`${prompt}, high quality, detailed`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const response = await fetch(
        `https://api-inference.huggingface.co/models/${model}`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${API_TOKEN}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            inputs: enhancedPrompt,
            parameters: {
              num_inference_steps: 30,
              guidance_scale: 7.5
            }
          }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erro na API do Hugging Face: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const base64Image = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });

      if (isSubscriptionLimited && subscription?.user_id) {
        try {
          const { error: updateError } = await supabase
            .from('subscriptions')
            .update({ request_count: subscription.request_count - 1 })
            .eq('user_id', subscription.user_id);

          if (updateError) {
            console.error('Error updating request count:', updateError);
          }
        } catch (dbError) {
          console.error('Database error:', dbError);
        }
      }

      return [{
        urls: {
          regular: base64Image,
          full: base64Image
        },
        alt_description: `Imagem gerada por IA para: ${prompt}`
      }];
    } catch (error) {
      console.error(`Erro ao gerar imagens com Hugging Face (modelo ${model}):`, error);
      throw error;
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setError('');
    setImages([]);

    try {
      const englishQuery = translateQuery(query.trim());
      setTranslatedQuery(englishQuery);

      if (searchMode === 'unsplash') {
        const result = await unsplash.search.getPhotos({
          query: englishQuery,
          perPage: 20,
          orientation: 'landscape'
        });

        if (result.errors) {
          throw new Error(result.errors[0]);
        }

        const modifiedResults = result.response?.results.map(img => ({
          ...img,
          alt_description: `Imagem de ${englishQuery}`,
          user: undefined
        })) || [];

        setImages(modifiedResults);
      } 
      else if (searchMode === 'pixabay') {
        const pixabayResults = await searchPixabayImages(englishQuery);
        setImages(pixabayResults);
      } 
      else if (searchMode === 'ai') {
        if (isSubscriptionLimited && !hasRequestsAvailable) {
          throw new Error('Você atingiu o limite de requisições do seu plano');
        }

        const aiImages = await generateImageWithAI(query.trim());
        setImages(aiImages);

        if (isSubscriptionLimited && subscription?.user_id) {
          try {
            const { error: updateError } = await supabase
              .from('subscriptions')
              .update({ request_count: subscription.request_count - 1 })
              .eq('user_id', subscription.user_id);

            if (updateError) {
              console.error('Error updating request count:', updateError);
            }
          } catch (dbError) {
            console.error('Database error:', dbError);
          }
        }
      }
      else if (searchMode === 'huggingface') {
        try {
          const hfImages = await generateImageWithHuggingFace(query.trim(), hfModel);
          setImages(hfImages);
        } catch (firstError) {
          const alternativeModel = huggingFaceModels.find(m => m.value !== hfModel)?.value;
          if (alternativeModel) {
            try {
              setError('Tentando com outro modelo de IA...');
              const hfImages = await generateImageWithHuggingFace(query.trim(), alternativeModel);
              setImages(hfImages);
              setError('');
            } catch (secondError) {
              throw new Error('Falha ao gerar imagem com os modelos de IA. Tente novamente mais tarde.');
            }
          } else {
            throw new Error('Falha ao gerar imagem com o modelo de IA. Tente novamente mais tarde.');
          }
        }
      }
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar imagens';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (imageUrl: string) => {
    try {
      if (imageUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `ai-image-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      
      if (searchMode === 'unsplash' || searchMode === 'pixabay') {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error('Falha ao baixar imagem');
        const blob = await response.blob();
        saveBlob(blob);
      }
    } catch (err) {
      setError('Erro ao baixar imagem. Tente clicar com o botão direito e "Salvar imagem como..."');
    }
  };

  const saveBlob = (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `image-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const huggingFaceModels = [
    { value: 'black-forest-labs/FLUX.1-dev', label: 'Padrão' },
    { value: 'stabilityai/stable-diffusion-xl-base-1.0', label: 'Stable'}
  ];

  return (
    <div className="space-y-6 w-full overflow-hidden">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Digite sua busca em português..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={searchMode}
            onChange={(e) => setSearchMode(e.target.value as 'unsplash' | 'pixabay' | 'ai' | 'huggingface')}
            className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
          >
            <option value="pixabay">Padrão</option>
            <option value="unsplash">Server 2</option>
            {/*<option value="huggingface">IA</option>*/}
          </select>

          {searchMode === 'huggingface' && (
            <select
              value={hfModel}
              onChange={(e) => setHfModel(e.target.value)}
              className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
            >
              {huggingFaceModels.map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          )}

          <button
            onClick={handleSearch}
            disabled={loading || !query.trim() || ((searchMode === 'ai' || searchMode === 'huggingface') && isSubscriptionLimited && !hasRequestsAvailable)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="sm:inline hidden">Gerando...</span>
                <span className="sm:hidden inline">...</span>
              </>
            ) : (
              <>
                <Search className="h-5 w-5" />
                <span className="sm:inline hidden">Gerar</span>
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="ml-3 text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-4">
        {images.map((image, index) => (
          <div key={index} className="relative group">
            <img
              src={image.urls.regular}
              alt={image.alt_description || 'Imagem'}
              className="w-full h-auto object-contain rounded-lg max-h-[512px]"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (!target.src.startsWith('data:')) {
                  target.src = image.urls.raw || image.urls.full;
                }
              }}
            />
            <button
              onClick={() => handleDownload(image.urls.raw || image.urls.full)}
              className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-800 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Download className="h-5 w-5 text-gray-700 dark:text-gray-200" />
            </button>
          </div>
        ))}
      </div>

      {(searchMode === 'ai' || searchMode === 'huggingface') && isSubscriptionLimited && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Requisições restantes: {subscription?.request_count || 0}
        </div>
      )}
    </div>
  );
};

export default ImageSearch;